import theme, { colors } from '@globals/themes/defaultTheme'
import Autocomplete, {
  AutocompleteRenderGroupParams,
  AutocompleteRenderValueGetItemProps,
} from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import FieldLabel from '@shared/forms/FieldLabel'
import { isValidSearchInput } from '@shared/utils/searchInputValidation'
import { useT } from '@transifex/react'
import { ReactElement, ReactNode, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

export const textFieldStyle: SxProps = {
  '& input': {
    '&::placeholder': {
      color: colors.grayMedium,
      opacity: 1,
    },
  },
}

export type AutoCompleteOption<T extends object | string = string> = {
  id: string
  value: T
  icon?: ReactElement
  avatar?: ReactElement
}

export type AutoCompleteResultsZIndexPlacement = 'modal' | 'page' | 'drawer'

export type AutoCompleteRenderChipProps = {
  key?: number
} & ReturnType<AutocompleteRenderValueGetItemProps<false>>

type StandardAutoCompleteSearchProps<T extends object | string = string> = {
  value: AutoCompleteOption<T>[] | AutoCompleteOption<T>
  onSearchTextChanged: (searchText: string) => void
  onSelectedOptionsChanged: (option: AutoCompleteOption<T>[]) => void
  label?: ReactNode
  placeholder: string
  searchText: string
  options: AutoCompleteOption<T>[]
  renderChip: (
    option: AutoCompleteOption<T>,
    props: AutoCompleteRenderChipProps,
    index?: number,
  ) => ReactNode
  renderOption?: (props: object, option: AutoCompleteOption<T>) => ReactNode
  groupBy?: (option: AutoCompleteOption<T>) => string
  id?: string
  multiple?: boolean
  disableClose?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
  debounceInput?: boolean
  loading?: boolean
  hideDropdown?: boolean
  readOnly?: boolean
}

const groupHeaderStyle: SxProps = {
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  height: 30,
  color: colors.grayPrimary,
  background: colors.grayUltraLight,
}

const placementZIndex: Record<AutoCompleteResultsZIndexPlacement, number> = {
  page: 1,
  modal: theme.zIndex.modal + 1,
  drawer: theme.zIndex.drawer + 1,
}

const AUTO_COMPLETE_DEBOUNCE_TIME = 250

const StandardAutoCompleteSearch = <T extends object | string = string>({
  onSearchTextChanged,
  onSelectedOptionsChanged,
  label,
  placeholder,
  searchText,
  options,
  renderChip,
  renderOption,
  groupBy,
  id = 'autocompleteSearch',
  multiple = true,
  disabled = false,
  value,
  disableClose,
  zIndexPlacement = 'page',
  debounceInput = true,
  loading,
  hideDropdown = false,
  readOnly = false,
}: StandardAutoCompleteSearchProps<T>) => {
  const t = useT()
  const renderGroup = (params: AutocompleteRenderGroupParams) => (
    <Stack key={params.key}>
      <Typography variant="caption" sx={groupHeaderStyle}>
        {params.group}
      </Typography>
      <Box>{params.children}</Box>
    </Stack>
  )
  const [inputValue, setInputValue] = useState<string>('')
  const [deferredInputValue] = useDebounce(
    inputValue,
    debounceInput ? AUTO_COMPLETE_DEBOUNCE_TIME : 0,
  )

  useEffect(() => {
    if (isValidSearchInput(deferredInputValue)) {
      onSearchTextChanged(deferredInputValue)
    }
  }, [deferredInputValue])

  useEffect(() => {
    setInputValue(searchText)
  }, [searchText])

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    setIsOpen(deferredInputValue.trim() !== '')
  }, [deferredInputValue])

  return (
    <Stack direction="column" useFlexGap gap={0.25}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Autocomplete
        open={isOpen}
        // Ensures that we do not show "no results" when simply clicking on
        // the auto complete component. This get's triggered when the component is
        // requested to open (via clicking the auto complete OR clicking the dropdown icon)
        onOpen={() => setIsOpen(deferredInputValue.trim() !== '' || options.length > 0)}
        onClose={() => setIsOpen(false)}
        sx={textFieldStyle}
        multiple={multiple}
        disableCloseOnSelect={disableClose}
        id={id}
        filterOptions={option => option}
        value={value}
        options={options}
        autoComplete
        fullWidth
        groupBy={groupBy}
        renderGroup={groupBy ? renderGroup : undefined}
        getOptionLabel={option =>
          (!renderOption && (option as AutoCompleteOption<string>).value) || ''
        }
        inputValue={inputValue}
        onInputChange={(_, newText, reason) => {
          if (reason !== 'reset') {
            setInputValue(newText)
          }
        }}
        loading={loading}
        loadingText={t('Loading results...')}
        noOptionsText={t('No results found')}
        onChange={(_, newOptions) => {
          if (multiple) {
            onSelectedOptionsChanged(newOptions as AutoCompleteOption<T>[])
          } else {
            onSelectedOptionsChanged(
              newOptions !== null ? [newOptions as AutoCompleteOption<T>] : [],
            )
          }
        }}
        onKeyDown={e => {
          if (!multiple && searchText === '' && value && e.key === 'Backspace') {
            onSelectedOptionsChanged([])
          }
        }}
        renderValue={(value, getItemProps) => {
          if (multiple) {
            return (value as AutoCompleteOption<T>[]).map((value, index) =>
              renderChip(value, getItemProps({ index }), index),
            )
          } else {
            return renderChip(value as AutoCompleteOption<T>, getItemProps({ index: 0 }))
          }
        }}
        renderOption={renderOption}
        renderInput={params => {
          return (
            <TextField
              {...params}
              fullWidth
              placeholder={!disabled ? placeholder : ''}
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                },
                htmlInput: {
                  ...params.inputProps,
                  readOnly,
                },
              }}
            />
          )
        }}
        disabled={disabled}
        forcePopupIcon={hideDropdown ? false : undefined}
        slotProps={{
          popper: {
            sx: {
              zIndex: placementZIndex[zIndexPlacement],
            },
          },
        }}
      />
    </Stack>
  )
}

export default StandardAutoCompleteSearch
